import type { EventConfig } from '@/types'
import { useState, useEffect } from 'react'
import { Upload, Trash2, BarChart2, Users, Image as ImageIcon } from 'lucide-react'
import { uploadFile } from '@/lib/utils'

interface EventOverviewProps {
  event: EventConfig
  onChange: (updates: Partial<EventConfig>) => void
}

export default function EventOverview({ event, onChange }: EventOverviewProps) {
  const [uploading, setUploading] = useState(false)
  const [stats, setStats] = useState({ galleryImages: 0, totalSessions: 0, printsTriggered: 0 })

  useEffect(() => {
    fetch(`/api/events/${event.id}/stats`)
      .then(res => res.json())
      .then(data => {
        if (!data.error) setStats(data)
      })
      .catch(console.error)
  }, [event.id])

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const url = await uploadFile(file)
      if (url) {
        onChange({ coverImage: url })
      }
    } catch (err) {
      console.error('Failed to upload cover image', err)
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = () => {
    onChange({ coverImage: null })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{event.name}</h1>
        <p className="text-sm text-muted-foreground">ID: {event.id}</p>
      </div>

      {/* Cover Image Section */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="p-4 border-b border-border">
          <h2 className="text-sm font-semibold">Event Cover Image</h2>
          <p className="text-xs text-muted-foreground">This image will be shown on the event card in the main dashboard.</p>
        </div>
        <div className="p-4">
          <div className="relative h-48 w-full rounded-lg overflow-hidden bg-muted flex items-center justify-center border border-dashed border-border">
            {event.coverImage ? (
              <>
                <img src={event.coverImage} className="absolute inset-0 h-full w-full object-cover" alt="Cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <label className="cursor-pointer flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20 transition-colors">
                    <Upload className="h-3.5 w-3.5" />
                    Change
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                  </label>
                  <button 
                    onClick={handleRemove}
                    className="flex items-center gap-1.5 rounded-lg bg-red-500/20 px-3 py-1.5 text-xs font-medium text-red-400 hover:bg-red-500/30 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Remove
                  </button>
                </div>
              </>
            ) : (
              <label className="cursor-pointer flex flex-col items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <Upload className="h-8 w-8" />
                <span className="text-sm font-medium">{uploading ? 'Uploading...' : 'Upload Cover Image'}</span>
                <span className="text-xs">Recommended size: 800x200 (4:1)</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Stats Section (Placeholders) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl border border-border p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400">
            <ImageIcon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-2xl font-bold">{stats.galleryImages}</div>
            <div className="text-xs text-muted-foreground">Gallery Images</div>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <div className="text-2xl font-bold">{stats.totalSessions}</div>
            <div className="text-xs text-muted-foreground">Total Sessions</div>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-400">
            <BarChart2 className="h-5 w-5" />
          </div>
          <div>
            <div className="text-2xl font-bold">{stats.printsTriggered}</div>
            <div className="text-xs text-muted-foreground">Prints Triggered</div>
          </div>
        </div>
      </div>
    </div>
  )
}
